import { NgModule } from '@angular/core';
import { RouterModule, Routes, mapToCanActivate, mapToCanDeactivate } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { PortalAdminGuard } from '@core/guards';
import { MainComponent } from '@feature/main/main.component';
import { AccessPageGuard } from '@mynexus/mynexus-ui-lib';
import { HomeComponent } from '@pages/home/home.component';
import { MicroAppComponent } from '@shared/component/micro-app/micro-app.component';
import { LineOfBusinessResolver } from '@core/resolvers/line-of-business.resolver';
import DMEPOSRoutes from '@products/dmepos/routes';
import PACRoutes from '@products/pacim/routes';
import HHRoutes from '@products/hh/routes';
import CoreRoutes from '@products/core/routes';
import { NotesGuard } from './core';

const routes: Routes = [
  {
    path: '',
    component: MainComponent,
    resolve: { lineOfBusiness: LineOfBusinessResolver },
    children: [
      {
        path: 'home',
        component: HomeComponent,
        data: { title: 'Home' },
        canActivate: mapToCanActivate([MsalGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: ':tenantId/member/:subscriberNumber',
        loadChildren: () => import('@feature/member-detail/member-detail.module')
          .then((m) => m.MemberDetailModule),
        data: {
          title: 'Member Details',
          showNotesPanel: true,
          permissions: ['MemberPage.UI'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'activity-center',
        loadComponent: () => import('@pages/activity-center/activity-center.component').then((m) => m.ActivityCenterComponent),
        data: {
          title: 'Activity Center',
          permissions: ['ActivityCenterPage.UI'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'claims',
        loadChildren: () =>
          import('@feature/claim-list/claim-list.module').then(
            (m) => m.ClaimListModule
          ),
        data: { title: 'Claims' },
        canActivate: mapToCanActivate([MsalGuard]),
      },
      {
        path: 'claims-search-by-details',
        loadChildren: () => import('@feature/claims-search-list/claims-search-list.module')
          .then((m) => m.ClaimsSearchListModule),
        data: { title: 'Claims Search By Details' },
        canActivate: mapToCanActivate([MsalGuard]),
      },
      {
        path: ':tenantId/AuthorizationsViewList',
        loadChildren: () => import('@feature/authorizations-history/authorizations-history.module')
          .then((m) => m.AuthorizationsHistoryModule),
        data: {
          title: 'Authorization History',
          permissions: ['AuthorizationHistoryPage.UI'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'FaxHistory',
        loadChildren: () => import('@feature/documents-history/document-history.module')
          .then((m) => m.DocumentHistoryModule),
        data: {
          title: 'Fax History',
          permissions: ['FaxHistoryPage.UI'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'advanced-search',
        loadChildren: () => import('@feature/advanced-search/advanced-search.module')
          .then((m) => m.AdvancedSearchModule),
        data: {
          title: 'Advanced Search',
          permissions: [
            'AdvancedSearchMember.Read',
            'AdvancedSearchServiceProvider.Read',
            'AdvancedSearchOrderingFollowingProvider.Read',
          ],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'AdminPanel',
        loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
        data: {
          title: 'Admin',
          permissions: ['AdminPage.UI', 'LetterMaps.Read'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
      },
      {
        path: 'daily-dashboard',
        loadChildren: () => import('@feature/daily-dashboard').then((m) => m.DailyDashboardModule),
        data: { permissions: [
          'HHDailyDashboard.Read',
          'PACMADailyDashboard.Read',
          'PACCOMDailyDashboard.Read',
          'DMEPOSMedicareDailyDashboard.Read',
          'DMEPOSMedicaidDailyDashboard.Read',
        ] },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
      },
      {
        path: 'InboundFaxes',
        loadChildren: () => import('@feature/inbound-fax/inbound-fax.module')
          .then((m) => m.InboundFaxModule),
        data: {
          title: 'Inbound Fax',
          permissions: ['InboundFax.UI.Read'],
        },
        canActivate: mapToCanActivate([MsalGuard, AccessPageGuard]),
        canDeactivate: mapToCanDeactivate([NotesGuard]),
      },
      {
        path: 'notifications',
        loadComponent: () => import('@pages/notifications/notifications.component').then(m => m.NotificationsPageComponent),
        data: {
          title: 'Notifications',
        },
        canActivate: mapToCanActivate([MsalGuard]),
      },
      ...DMEPOSRoutes,
      ...PACRoutes,
      ...HHRoutes,
      ...CoreRoutes,
      {
        path: '**',
        component: MicroAppComponent,
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    component: MicroAppComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [PortalAdminGuard],
})
export class AppRoutingModule { }
